import { SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import { useCallback } from "react";
import { RESPONDENTS_TYPES_DESCRIPTION, RespondentType } from "@models/RespondentType";
import {
    useBrands,
    useCStore,
    useDeliveryServices,
    useHotels,
    useOnSites,
    useRespondentTypes,
    useRestaurants,
    useSegments,
    useSupermarkets
} from "@core/CommonDataContext";
import { filterItemsByKey } from "@core/utils/array";

export function useGetSelectedOptionsNames(useDescription = false) {
    const respondentTypeOptions = useRespondentTypes();
    const restaurantsOptions = useRestaurants();
    const cStoreOptions = useCStore();
    const supermarketsOptions = useSupermarkets();
    const hotelsOptions = useHotels();
    const brandsOptions = useBrands();
    const segmentsOptions = useSegments();
    const onSiteOptions = useOnSites();
    const deliveryOptions = useDeliveryServices();

    const getRespondentTypeNames = useCallback((data: SelectAmongData) => {
        return filterItemsByKey(respondentTypeOptions, data.respondentTypes as typeof respondentTypeOptions[number]['id'][]).map(
            item => useDescription ? RESPONDENTS_TYPES_DESCRIPTION[item.id as RespondentType] : item.name ?? ''
        );
    }, [respondentTypeOptions, useDescription]);

    const getUserGroupNames = useCallback((data: SelectAmongData) => {
        return [
            ...filterItemsByKey(restaurantsOptions, data.restaurants),
            ...filterItemsByKey(cStoreOptions, data.cStores),
            ...filterItemsByKey(supermarketsOptions, data.supermarkets),
        ].map(
            item => item.name ? `${item.name} users` : ''
        );
    }, [restaurantsOptions, cStoreOptions, supermarketsOptions]);

    const getBuyerGroupNames = useCallback((data: SelectAmongData) => {
        return filterItemsByKey(brandsOptions, data.brands).map(
            item => item.name ? `${item.name} buyers` : ''
        );
    }, [brandsOptions]);

    const getVisitorGroupNames = useCallback((data: SelectAmongData) => {
        return [
            ...filterItemsByKey(hotelsOptions, data.hotels),
            ...filterItemsByKey(segmentsOptions, data.segmentsAndOnSites),
            ...filterItemsByKey(onSiteOptions, data.segmentsAndOnSites as typeof onSiteOptions[number]['id'][]),
            ...filterItemsByKey(deliveryOptions, data.deliveryServices as typeof deliveryOptions[number]['id'][]),
        ].map(
            item => item.name ? `${item.name} visitors` : ''
        );
    }, [hotelsOptions, segmentsOptions, onSiteOptions, deliveryOptions]);

    return useCallback(
        (data: SelectAmongData) => [
            ...getRespondentTypeNames(data),
            ...getUserGroupNames(data),
            ...getBuyerGroupNames(data),
            ...getVisitorGroupNames(data),
        ],
        [getRespondentTypeNames, getUserGroupNames, getBuyerGroupNames, getVisitorGroupNames]
    );
}